import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faBuilding,
  faChevronDown,
  faChevronUp,
  faEnvelope,
  faGlobe,
  faPhone,
  faPhoneVolume,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import type { Customer } from '@/services/customer.service';

/**
 * Read-only answer to one question: who is this customer?
 *
 * Deliberately inert — no edit buttons, no dropdowns, no modals. Everything on
 * this screen that CAN be changed lives below in Service Information and
 * Delivery & Billing; if this block offered an edit too, an operator would have
 * to work out which of two places owns the address.
 *
 * Collapsed deliberately omits the street lines. This is a legal identity, not a
 * destination — showing a full street address here is what makes it look like a
 * fourth address competing with Shipping and Billing. City, state and GSTIN are
 * enough to recognise the company; the street is available on request.
 */
export function CustomerSummary({ customer }: { customer: Customer }) {
  const [expanded, setExpanded] = useState(false);

  const cityLine = [customer.city, [customer.state, customer.postalCode].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');

  const streetLines = [customer.addressLine1 ?? customer.address, customer.addressLine2].filter(
    (v): v is string => !!v,
  );

  return (
    <div className="rounded-lg border border-border bg-muted/30 px-3 py-2.5 text-[13px]">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faBuilding} className="h-3 w-3 text-muted-foreground" />
            <span className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
              Registered Address
            </span>
          </div>
          {/* Names what this is, so it is not mistaken for a fourth address. */}
          <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground/80">
            Customer&rsquo;s legal address and GST registration.
          </p>
        </div>

        {/* Top-right: reachable without reading past the content it controls. */}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md px-1.5 py-0.5 text-[12px] font-semibold text-primary transition-colors duration-200 hover:bg-muted"
        >
          <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} className="h-2.5 w-2.5" />
          {expanded ? 'Less' : 'More'}
        </button>
      </div>

      <p className="mt-2 text-sm font-bold text-foreground">{customer.name}</p>

      <div className="mt-0.5 leading-relaxed text-muted-foreground">
        {expanded && streetLines.map((l, i) => <div key={i}>{l}</div>)}
        {cityLine && <div>{cityLine}</div>}
        {expanded && customer.country && <div>{customer.country}</div>}
      </div>

      {customer.gstNumber && (
        <p className="mt-1 font-medium text-foreground">GSTIN: {customer.gstNumber}</p>
      )}

      <div className="mt-1.5 space-y-1 text-muted-foreground">
        <Line icon={faPhone} value={customer.phone} />
        <Line icon={faEnvelope} value={customer.email} />
        {expanded && (
          <>
            <Line icon={faPhoneVolume} label="Alternate" value={customer.alternatePhone} />
            <Line icon={faUser} label="Primary contact" value={customer.primaryContactName} />
            <Line icon={faGlobe} value={customer.website} />
          </>
        )}
      </div>
    </div>
  );
}

/** Renders nothing when the field is empty, rather than an icon with a blank. */
function Line({
  icon,
  label,
  value,
}: {
  icon: IconDefinition;
  label?: string;
  value?: string | null;
}) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2">
      <FontAwesomeIcon icon={icon} className="mt-[3px] h-3 w-3 shrink-0" />
      <span className="min-w-0 break-words">
        {label ? `${label}: ` : ''}
        {value}
      </span>
    </div>
  );
}
